<template>
  <div>
    <div v-if="!pageLoading">
      <v-card flat class="tw-mt-5 md:tw-mt-16">
        <div class="tw-px-4 md:tw-px-8">
          <card-title icon="mdi-account">Quote Summary</card-title>

          <!-- Enhanced Quote Summary -->
          <v-card flat color="primary" class="c-bg-navy tw-p-3 md:tw-p-6 rounded-lg tw-mt-5" elevation="8">
            <div class="tw-flex tw-flex-wrap md:tw-flex-no-wrap tw-items-center">
              <div class="tw-w-full md:tw-w-1/4 tw-text-center md:tw-text-left">
                <v-icon size="80" color="white" class="tw-mb-4">mdi-airplane</v-icon>
                <div class="tw-w-full">
                  <h3 class="tw-text-white tw-font-bold tw-text-lg">TRAVEL INSURANCE</h3>
                  <p class="tw-text-blue-100 tw-text-sm tw-mt-1">Complete protection for your journey</p>
                </div>
              </div>
              <div class="tw-text-white tw-w-full md:tw-w-3/4">
                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                  <div class="tw-bg-black tw-bg-opacity-20 tw-p-4 tw-rounded-lg">
                    <h4 class="tw-text-white tw-font-semibold tw-mb-3 tw-flex tw-items-center">
                      <v-icon size="20" color="white" class="tw-mr-2">mdi-map-marker</v-icon>
                      Travel Details
                    </h4>
                    <div class="tw-space-y-2">
                      <div v-if="quote.destination" class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Destination: {{ quote.destination }}</small>
                      </div>
                      <div v-if="quote.travel_type" class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Travel Type: {{ quote.travel_type | capitalize }}</small>
                      </div>
                      <div v-if="quote.start_date && quote.end_date" class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Dates: {{ formatDateRange(quote.start_date, quote.end_date) }}</small>
                      </div>
                      <div v-if="quote.number_of_travelers" class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Travelers: {{ quote.number_of_travelers }}</small>
                      </div>
                    </div>
                  </div>

                  <div class="tw-bg-black tw-bg-opacity-20 tw-p-4 tw-rounded-lg">
                    <h4 class="tw-text-white tw-font-semibold tw-mb-3 tw-flex tw-items-center">
                      <v-icon size="20" color="white" class="tw-mr-2">mdi-shield-check</v-icon>
                      Coverage Details
                    </h4>
                    <div class="tw-space-y-2">
                      <div class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Medical Expenses - USD {{ quote.coverage_amount | formatNum }}</small>
                      </div>
                      <div class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Trip Cancellation - USD {{ (quote.coverage_amount * 0.8) | formatNum }}</small>
                      </div>
                      <div class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Luggage Protection - USD {{ (quote.coverage_amount * 0.1) | formatNum }}</small>
                      </div>
                      <div class="tw-flex tw-items-center">
                        <v-icon size="15" color="white" class="tw-mr-2">mdi-check-circle</v-icon>
                        <small class="tw-text-white">Emergency Evacuation - USD {{ 1000000 | formatNum }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <hr class="tw-my-4">
                <div class="tw-flex tw-justify-between tw-items-center">
                  <div>
                    <h5 class="tw-text-white tw-text-xl tw-font-bold">Premium Payable: USD {{ quote.premium | formatNum }}</h5>
                    <p class="tw-text-blue-100 tw-text-sm tw-mt-1">Complete your details below to proceed</p>
                  </div>
                  <v-chip color="success" size="large">
                    <v-icon start size="16">mdi-check</v-icon>
                    Quote Ready
                  </v-chip>
                </div>
              </div>
            </div>
          </v-card>
          <!-- Buying for Someone Else Notice -->
          <div v-if="isBuyingForSomeoneElse" class="tw-mb-6">
            <v-alert
              type="info"
              variant="tonal"
              prominent
              class="tw-mb-4"
            >
              <template v-slot:prepend>
                <v-icon>mdi-account-heart</v-icon>
              </template>
              <div>
                <h4 class="tw-font-semibold tw-mb-2">Buying Travel Insurance for Someone Else</h4>
                <p class="tw-mb-2">
                  You are purchasing this policy for another person. Please enter the traveler's information below.
                  The traveler will be the policyholder and will receive the policy documents.
                </p>
                <div class="tw-bg-white tw-rounded-lg tw-p-3 tw-mt-3 tw-border tw-border-blue-200">
                  <div class="tw-flex tw-items-center tw-text-sm tw-text-blue-800">
                    <v-icon color="blue" size="16" class="tw-mr-2">mdi-information</v-icon>
                    <span><strong>Note:</strong> You (buyer) will handle payment, but the traveler will be the insured person and policy owner.</span>
                  </div>
                </div>
              </div>
            </v-alert>
          </div>

          <card-title icon="mdi-account">{{ displayTitle }}</card-title>

          <div v-if="displaySubtitle" class="tw-text-gray-600 tw-text-sm tw-mb-4 tw-px-2 md:tw-px-8">
            {{ displaySubtitle }}
          </div>

          <!-- Progress indicator -->
          <div class="tw-mt-6 tw-mb-4">
            <v-progress-linear
              :model-value="getFormCompletionPercentage()"
              color="primary"
              height="8"
              rounded
            ></v-progress-linear>
            <p class="tw-text-sm tw-text-gray-600 tw-mt-2 tw-text-center">
              Form completion: {{ getFormCompletionPercentage() }}%
            </p>
          </div>

          <div class="tw-px-4 md:tw-px-12 tw-mt-4 md:tw-mt-10">
            <v-form v-model="isValid">

              <!-- Basic Information Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">
                  {{ isBuyingForSomeoneElse ? 'Traveler Information' : 'Basic Information' }}
                </h3>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                  <v-text-field
                    v-model="form.first_name"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="First Name"
                    variant="outlined"
                    :rules="[required('First Name')]"
                  ></v-text-field>

                  <v-text-field
                    v-model="form.last_name"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Last Name"
                    variant="outlined"
                    :rules="[required('Last Name')]"
                  ></v-text-field>

                  <v-menu
                    v-model="dateOfBirthMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateOfBirthFormatted"
                        label="Date of Birth"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        variant="outlined"
                        class="my-2 tw-px-0 md:tw-px-6"
                        :rules="[required('Date of Birth'), validateDateOfBirth()]"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.date_of_birth"
                      :max="new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().slice(0, 10)"
                      no-title
                      @input="dateOfBirthMenu = false"
                      color="primary"
                    ></v-date-picker>
                  </v-menu>

                  <v-text-field
                    v-model="form.email"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    :rules="[required('Email Address'), emailFormat()]"
                    :readonly="isAuthenticated"
                  ></v-text-field>

                  <v-select
                    v-model="form.gender"
                    :items="[{text: 'Male', value: 'M'}, {text: 'Female', value: 'F'}, {text: 'Other', value: 'O'}]"
                    item-title="text"
                    item-value="value"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Gender"
                    variant="outlined"
                    :rules="[required('Gender')]"
                  ></v-select>

                  <v-text-field
                    v-model="form.passport_number"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Passport No."
                    variant="outlined"
                  ></v-text-field>

                  <!-- KRA PIN field for residents only -->
                  <v-text-field
                    v-if="isAuthenticated && !isBuyingForSomeoneElse"
                    v-model="form.kra_pin"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="KRA PIN No."
                    variant="outlined"
                    :rules="[required('KRA PIN')]"
                    :readonly="isAuthenticated"
                  ></v-text-field>

                  <!-- KRA PIN field for traveler when buying for someone else (optional) -->
                  <v-text-field
                    v-if="isBuyingForSomeoneElse"
                    v-model="form.kra_pin"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Traveler's KRA PIN No. (Optional)"
                    variant="outlined"
                    hint="Enter if the traveler is a Kenyan resident"
                    persistent-hint
                  ></v-text-field>

                  <!-- ID Number field for residents only -->
                  <v-text-field
                    v-if="isAuthenticated && !isBuyingForSomeoneElse"
                    v-model="form.id_number"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="ID No."
                    type="number"
                    variant="outlined"
                    :rules="[required('ID No.'), nationalIdFormat()]"
                    :readonly="isAuthenticated"
                  ></v-text-field>

                  <!-- ID Number field for traveler when buying for someone else (optional) -->
                  <v-text-field
                    v-if="isBuyingForSomeoneElse"
                    v-model="form.id_number"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Traveler's ID No. (Optional)"
                    type="number"
                    variant="outlined"
                    hint="Enter if the traveler is a Kenyan resident"
                    persistent-hint
                    :rules="[nationalIdFormat(true)]"
                  ></v-text-field>
                </div>
              </div>

              <!-- Applicant Information Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Applicant Information</h3>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-px-0 md:tw-px-6" :class="{ 'tw-opacity-60 tw-pointer-events-none': referralFromQuery }">
                  <p class="tw-text-gray-600 tw-mb-4">The applicant is the principal/insured when creating the policy in MAPFRE.</p>
                  <p class="tw-text-gray-600 tw-mb-4 tw-font-semibold">Are you a direct client or referred to us by an Agent?</p>
                  <v-radio-group class="my-2" v-model="form.agent_option" :rules="[v => !!v || 'Selection choice is required']" row :disabled="referralFromQuery">
                    <v-radio label="Direct client" :value="1" class="tw-mt-0 md:tw-mt-5 tw-pr-10"></v-radio>
                    <v-radio label="Referred by Agent" :value="2" class="tw-mt-0 md:tw-mt-5"></v-radio>
                  </v-radio-group>
                </div>
              </div>

              <!-- Contact Information Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Contact Information</h3>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                  <v-text-field
                    v-model="form.phone_number"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Mobile Number (Main)"
                    variant="outlined"
                    :rules="[required('Mobile No.'), isAuthenticated ? mobileFormat() : internationalMobileFormat()]"
                  ></v-text-field>

                  <v-text-field
                    v-model="form.mobile_alternative"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Mobile Number (Alternative)"
                    variant="outlined"
                  ></v-text-field>
                </div>
              </div>

              <!-- Residential Address Section -->
              <div class="tw-w-full tw-mb-8">
                <v-card class="tw-mb-6" outlined elevation="2">
                  <v-card-title class="c-blue-text tw-text-lg tw-bg-gray-50">
                    <v-icon class="tw-mr-2">mdi-map-marker</v-icon>
                    Residential Address
                  </v-card-title>
                  <v-card-text class="tw-p-6">
                    <!-- Google Maps Search -->
                    <div class="tw-mb-6">
                      <h4 class="tw-text-base tw-font-semibold tw-mb-3 tw-text-gray-700">
                        <v-icon size="small" class="tw-mr-1">mdi-google-maps</v-icon>
                        Search Address (Google Maps)
                      </h4>
                      <v-text-field
                        v-model="form.address"
                        ref="address"
                        id="address"
                        label="Start typing your address..."
                        placeholder="Enter your residential address"
                        prepend-inner-icon="mdi-magnify"
                        variant="outlined"
                        class="tw-mb-4"
                        hint="Select from suggestions to auto-fill address fields"
                        persistent-hint
                        :rules="[required('Address')]"
                      ></v-text-field>
                      <v-text-field
                        type="text"
                        v-model="form.coordinates"
                        :rules="[isObjectField()]"
                        class="tw-hidden"
                      ></v-text-field>
                    </div>

                    <!-- Postal Information -->
                    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                      <v-text-field
                        v-model="form.postal_code"
                        class="my-2"
                        label="Postal Code"
                        variant="outlined"
                      ></v-text-field>

                      <v-text-field
                        v-model="form.zip_code"
                        class="my-2"
                        label="Zip Code"
                        variant="outlined"
                      ></v-text-field>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Next of Kin Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Next of Kin <span class="tw-text-red-500">*</span></h3>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                  <v-text-field
                    v-model="form.emergency_contact_name"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Name *"
                    variant="outlined"
                    :rules="[required('Next of Kin Name')]"
                  ></v-text-field>

                  <v-text-field
                    v-model="form.emergency_contact_phone"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Phone Number *"
                    variant="outlined"
                    :rules="[required('Next of Kin Phone'), isAuthenticated ? mobileFormat() : internationalMobileFormat()]"
                  ></v-text-field>

                  <v-select
                    v-model="form.next_of_kin_relationship"
                    :items="['Parent', 'Spouse', 'Sibling', 'Child', 'Relative', 'Friend', 'Other']"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Relationship *"
                    variant="outlined"
                    :rules="[required('Relationship')]"
                  ></v-select>
                </div>
              </div>

              <!-- Political Exposure Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Political Exposure</h3>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-px-0 md:tw-px-6">
                  <p class="tw-text-gray-600 tw-mb-4 tw-font-semibold">Are you politically exposed?</p>
                  <v-radio-group class="my-2" v-model="form.politically_exposed" :rules="[v => v !== null && v !== undefined || 'Political exposure selection is required']" row>
                    <v-radio label="Yes" :value="true" class="tw-mt-0 md:tw-mt-5 tw-pr-10"></v-radio>
                    <v-radio label="No" :value="false" class="tw-mt-0 md:tw-mt-5"></v-radio>
                  </v-radio-group>
                </div>
              </div>

              <!-- Travel Details Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Travel Details</h3>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                  <v-menu
                    v-model="travelStartMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="travelStartFormatted"
                        label="Travel Start Date *"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        variant="outlined"
                        class="my-2 tw-px-0 md:tw-px-6"
                        :rules="[required('Travel Start Date')]"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.travel_start_date"
                      :min="new Date().toISOString().slice(0, 10)"
                      no-title
                      @input="travelStartMenu = false"
                      color="primary"
                    ></v-date-picker>
                  </v-menu>

                  <v-menu
                    v-model="travelEndMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="travelEndFormatted"
                        label="Travel End Date *"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        variant="outlined"
                        class="my-2 tw-px-0 md:tw-px-6"
                        :rules="[required('Travel End Date')]"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.travel_end_date"
                      :min="form.travel_start_date"
                      no-title
                      @input="travelEndMenu = false"
                      color="primary"
                    ></v-date-picker>
                  </v-menu>

                  <!-- Confirm Destination Country -->
                  <div class="my-2 tw-px-0 md:tw-px-6">
                    <v-select
                      v-model="form.destination_country"
                      :items="filteredDestinationCountries"
                      item-text="TRC_NAME"
                      item-value="TRC_SHT_DESC"
                      label="Confirm Destination Country *"
                      variant="outlined"
                      :rules="[required('Destination Country')]"
                      clearable
                      searchable
                      prepend-inner-icon="mdi-map-marker"
                      placeholder="Select your destination country"
                    ></v-select>
                    <small v-if="filteredDestinationCountries.length === 0 && !quote.destination" class="text-warning">Loading countries...</small>
                  </div>
                </div>
              </div>

              <!-- Document Uploads Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Document Uploads</h3>
                <div class="tw-px-2 md:tw-px-8 tw-mb-3">
                  <v-alert type="info" variant="outlined" class="tw-text-sm">
                    <v-icon slot="prepend">mdi-information</v-icon>
                    <strong>Required Documents:</strong>
                    <span v-if="isAuthenticated && !isBuyingForSomeoneElse">
                      ID Copy, KRA PIN, Passport, and Digital Signature are all required to proceed.
                    </span>
                    <span v-else>
                      Passport and Digital Signature are required to proceed.
                    </span>
                  </v-alert>
                </div>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <!-- ID Number/Copy Upload for authenticated users -->
                  <div v-if="isAuthenticated" class="tw-px-0 md:tw-px-6 tw-relative">
                    <div class="tw-flex tw-items-center tw-mb-3">
                      <h4 class="tw-text-base tw-font-semibold tw-text-gray-700">Upload ID Copy</h4>
                      <v-icon v-if="documentsUploaded.idNumber" color="success" size="20" class="tw-ml-2">mdi-check-circle</v-icon>
                      <v-icon v-else color="warning" size="20" class="tw-ml-2">mdi-clock-outline</v-icon>
                    </div>
                    <express-upload
                      :uid="form.id_number"
                      :phone-number="form.phone_number"
                      :full-name="form.first_name + ' ' + form.last_name"
                      :email="form.email"
                      name="ID Copy"
                      :size="5"
                      bucket="travel-policies"
                      travel-type="pacis"
                      document-type="idNumber"
                      :id-number="form.id_number"
                      :kra-pin="authenticatedUser['custom:kra-pin'] || form.kra_pin"
                      @upload-success="onUploadSuccess"
                    />
                    <p class="tw-text-xs tw-text-gray-500 tw-mt-2">maximum of 5 MB. PDF only</p>
                  </div>

                  <!-- KRA PIN Upload for authenticated users -->
                  <div v-if="isAuthenticated" class="tw-px-0 md:tw-px-6 tw-relative">
                    <div class="tw-flex tw-items-center tw-mb-3">
                      <h4 class="tw-text-base tw-font-semibold tw-text-gray-700">Upload KRA PIN</h4>
                      <v-icon v-if="documentsUploaded.kraPin" color="success" size="20" class="tw-ml-2">mdi-check-circle</v-icon>
                      <v-icon v-else color="warning" size="20" class="tw-ml-2">mdi-clock-outline</v-icon>
                    </div>
                    <express-upload
                      :uid="form.id_number"
                      :phone-number="form.phone_number"
                      :full-name="form.first_name + ' ' + form.last_name"
                      :email="form.email"
                      name="KRA PIN"
                      :size="5"
                      bucket="travel-policies"
                      travel-type="pacis"
                      document-type="kraPinNumber"
                      :id-number="form.id_number"
                      :kra-pin="authenticatedUser['custom:kra-pin'] || form.kra_pin"
                      @upload-success="onUploadSuccess"
                    />
                    <p class="tw-text-xs tw-text-gray-500 tw-mt-2">maximum of 5 MB. PDF only</p>
                  </div>

                  <!-- Passport Upload -->
                  <div class="tw-px-0 md:tw-px-6 tw-relative">
                    <div class="tw-flex tw-items-center tw-mb-3">
                      <h4 class="tw-text-base tw-font-semibold tw-text-gray-700">Upload Passport</h4>
                      <v-icon v-if="documentsUploaded.passport" color="success" size="20" class="tw-ml-2">mdi-check-circle</v-icon>
                      <v-icon v-else color="warning" size="20" class="tw-ml-2">mdi-clock-outline</v-icon>
                    </div>
                    <express-upload
                      :uid="form.id_number"
                      :phone-number="form.phone_number"
                      :full-name="form.first_name + ' ' + form.last_name"
                      :email="form.email"
                      name="Passport"
                      :size="5"
                      bucket="travel-policies"
                      :travel-type="isAuthenticated ? 'pacis' : 'non-resident'"
                      document-type="passport"
                      :id-number="form.id_number"
                      :kra-pin="isAuthenticated ? (authenticatedUser['custom:kra-pin'] || form.kra_pin) : null"
                      @upload-success="onUploadSuccess"
                    />
                    <p class="tw-text-xs tw-text-gray-500 tw-mt-2">maximum of 5 MB. PDF only</p>
                  </div>

                  <!-- Digital Signature Upload -->
                  <div class="tw-px-0 md:tw-px-6 tw-relative">
                    <div class="tw-flex tw-items-center tw-mb-3">
                      <h4 class="tw-text-base tw-font-semibold tw-text-gray-700">Upload Digital Signature</h4>
                      <v-icon v-if="documentsUploaded.signature" color="success" size="20" class="tw-ml-2">mdi-check-circle</v-icon>
                      <v-icon v-else color="warning" size="20" class="tw-ml-2">mdi-clock-outline</v-icon>
                    </div>
                    <express-upload
                      :uid="form.id_number"
                      :phone-number="form.phone_number"
                      :full-name="form.first_name + ' ' + form.last_name"
                      :email="form.email"
                      name="Digital Signature"
                      :size="5"
                      bucket="travel-policies"
                      :travel-type="isAuthenticated ? 'pacis' : 'non-resident'"
                      document-type="signature"
                      :id-number="form.id_number"
                      :kra-pin="isAuthenticated ? (authenticatedUser['custom:kra-pin'] || form.kra_pin) : null"
                      @upload-success="onUploadSuccess"
                    />
                    <p class="tw-text-xs tw-text-gray-500 tw-mt-2">maximum of 5 MB. PDF only</p>
                  </div>
                </div>
              </div>

              <!-- Declaration & Consent Section -->
              <div class="tw-w-full tw-mb-8">
                <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Declaration & Consent</h3>
                <hr class="tw-mt-3 tw-mb-5">

                <div class="tw-px-0 md:tw-px-6">
                  <v-checkbox
                    v-model="form.declaration_acceptance"
                    class="my-2"
                    :rules="[check('Declaration acceptance')]"
                  >
                    <template v-slot:label>
                      <div class="tw-text-sm">
                        <strong>Declaration & Consent:</strong> I hereby declare that the information provided is true and complete to the best of my knowledge.
                      </div>
                    </template>
                  </v-checkbox>

                  <v-checkbox
                    v-model="form.data_privacy_consent"
                    class="my-2"
                    :rules="[check('Data privacy consent')]"
                  >
                    <template v-slot:label>
                      <div class="tw-text-sm">
                        <strong>Data Privacy Consent:</strong> I consent to the processing of my personal data for insurance purposes.
                      </div>
                    </template>
                  </v-checkbox>
                </div>
              </div>

              <!-- Enhanced Submit Section -->
              <v-card class="tw-mt-6" color="grey-lighten-5" flat>
                <v-card-text class="tw-p-6">
                  <div class="tw-text-center tw-mb-4">
                    <h3 class="c-blue-text tw-text-lg tw-font-bold tw-mb-2">Ready to proceed?</h3>
                    <p class="tw-text-gray-600 tw-text-sm">Review your information and continue to secure payment</p>
                  </div>

                  <!-- Form validation summary -->
                  <div v-if="!formIsValid" class="tw-mb-4">
                    <v-alert type="warning" variant="outlined" class="tw-mb-4">
                      <strong>Please complete the following fields:</strong>
                      <ul class="tw-mt-2 tw-ml-4">
                        <li v-for="error in getFormErrors()" :key="error" class="tw-text-sm">
                          {{ error }}
                        </li>
                      </ul>
                    </v-alert>
                  </div>

                  <v-card-actions class="tw-justify-center tw-p-0">
                    <v-btn
                      size="x-large"
                      color="primary"
                      class="tw-px-12 tw-py-3"
                      @click="proceed"
                      :disabled="!formIsValid"
                      :loading="loading"
                      variant="flat"
                    >
                      <v-icon v-if="formIsValid" start>{{ isBuyingForSomeoneElse ? 'mdi-account-heart' : 'mdi-credit-card' }}</v-icon>
                      <v-icon v-else start>mdi-alert-circle</v-icon>
                      {{ formIsValid ? (isBuyingForSomeoneElse ? 'Continue as Buyer' : 'Proceed to Payment') : `Complete ${getFormErrors().length} field${getFormErrors().length === 1 ? '' : 's'}` }}
                      <v-icon v-if="formIsValid" end>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card-text>
              </v-card>

            </v-form>
          </div>
        </div>
      </v-card>
    </div>
    <div v-else>
      <v-skeleton-loader
          type="card"
      ></v-skeleton-loader>
    </div>
  </div>
</template>

<script>
import CardTitle from '@/components/shared/CardTitle';
import ExpressUpload from '@/components/shared/ExpressUpload';
import validations from '@/utils/validations';
import axios from 'axios';
import helpers from '@/utils/helpers';
import { mapGetters } from 'vuex';
import { TravelPolicyService } from '@/services/TravelPolicyService';
import { travelPaymentReference } from '@/utils/travelPaymentReference';
import { getReferralAgentCode, validateReferralCode } from '@/services/referralApi';

const { Loader } = require('google-maps');

const options = { version: '3.40', language: 'en', libraries: ['places'] };
const loader = new Loader(process.env.VUE_APP_GOOGLE_MAPS_API_KEY, options);

export default {
  name: 'PersonalDetailsTravel',
  components: { CardTitle, ExpressUpload },
  data: () => ({
    form: {
      first_name: '',
      last_name: '',
      phone_number: '',
      email: '',
      id_number: '',
      kra_pin: '',
      gender: '',
      date_of_birth: '',
      passport_number: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      next_of_kin_relationship: '',
      mobile_alternative: '',
      postal_code: '',
      zip_code: '',
      politically_exposed: null,
      travel_start_date: '',
      travel_end_date: '',
      destination_country: '',
      declaration_acceptance: false,
      data_privacy_consent: false,
      agent_option: 1,
      address: '',
      coordinates: null,
    },
    destinationCountries: [],
    quote: {},
    loading: false,
    pageLoading: true,
    documentsUploaded: {
      passport: false,
      signature: false,
      idNumber: false,
      kraPin: false,
    },
    isValid: false,
    autoSaveTimeout: null,
    dateOfBirthMenu: false,
    travelStartMenu: false,
    travelEndMenu: false,
    referralFromQuery: false,
    ...validations,
  }),
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),

    fromQuoteSummary() {
      // Check if user is coming from the standalone quote summary page
      return this.$route.query.fromQuoteSummary === 'true';
    },

    isBuyingForSomeoneElse() {
      // Check if this is a "buying for someone else" scenario
      return this.$route.query.buyingForSomeoneElse === 'true'
             || this.$route.name === 'BuyForSomeoneElseCover'
             || this.$route.path.includes('buy-for-someone-else');
    },

    isAuthenticated() {
      // Override isAuthenticated when buying for someone else - treat as non-resident flow
      if (this.isBuyingForSomeoneElse) {
        return false;
      }
      return this.$store.getters['auth/isAuthenticated'];
    },

    displayTitle() {
      if (this.isBuyingForSomeoneElse) {
        return 'Traveler Details (Policyholder)';
      }
      return 'Personal Details';
    },

    filteredDestinationCountries() {
      if (!this.quote || !this.quote.destination) {
        return this.destinationCountries;
      }

      const allCountries = this.destinationCountries;
      let zoneCode;

      // Define constants for worldwide regions
      /* eslint-disable sonarjs/no-duplicate-string */
      const WORLDWIDE_BASIC = 'Worldwide Basic';
      const WORLDWIDE_PREMIUM = 'Worlwide Premium';
      const WORLDWIDE_EXTRA = 'Worldwide Extra';

      // Map regions to zone codes based on quote destination
      switch (this.quote.destination) {
        case 'Africa':
          zoneCode = 1; // African countries
          break;
        case 'Asia':
          zoneCode = 2; // Asian countries
          break;
        case 'Europe':
          zoneCode = 4; // European countries
          break;
        case WORLDWIDE_BASIC:
        case WORLDWIDE_PREMIUM:
        case WORLDWIDE_EXTRA:
          zoneCode = 3; // Americas and Oceania countries
          break;
        default:
          return allCountries; // Return all countries if region not matched
      }

      return allCountries.filter((country) => country.TRC_TRZ_CODE === zoneCode);
    },

    displaySubtitle() {
      if (this.isBuyingForSomeoneElse) {
        return 'Enter the traveler\'s information who will be covered by this policy';
      }
      return 'Enter your personal information for the travel insurance policy';
    },

    // Check if all required documents are uploaded
    areRequiredDocumentsUploaded() {
      // Passport and signature are required for all users
      const basicRequirements = this.documentsUploaded.passport && this.documentsUploaded.signature;

      // For authenticated users (residents) and not buying for someone else, require all documents
      if (this.isAuthenticated && !this.isBuyingForSomeoneElse) {
        return basicRequirements && this.documentsUploaded.idNumber && this.documentsUploaded.kraPin;
      }

      // For non-residents or buying for someone else, only passport and signature are required
      return basicRequirements;
    },

    /* eslint-disable sonarjs/no-duplicate-string */
    formIsValid() {
      // Combine Vuetify form validation with custom validation
      // Set travel context for travel insurance policy auto-issuance after payment
      // For residents, use ID number; for non-residents, use passport number
      // const user = this.authenticatedUser;
      // const isResident = user && user['custom:id_number'];
      // const identificationDocument = isResident
      //   ? user['custom:id_number']
      //   : (this.form.id_number || 'UNKNOWN');

      const travelContext = {
        insuredFirstName: this.form.first_name || 'Unknown',
        insuredSurname: this.form.last_name || 'Unknown',
        // insuredFirstName: 'TEST',
        // insuredSurname: 'TEST',
        insuredPassport: this.form.passport_number,
        policyholderFirstName: this.form.first_name || 'Unknown',
        policyholderSurname: this.form.last_name || 'Unknown',
        policyholderPassport: this.form.passport_number,
        selectedCountry: this.quote?.destination_country || null,
      };
      // console.log('Travel context set:', travelContext);
      // console.log('Form destination_country:', this.form.destination_country);
      // console.log('Form destination_country:', this.quote?.destination_country);
      // console.log('Form Contents:', this.form);
      return this.isValid && this.getFormErrors().length === 0 && this.areRequiredDocumentsUploaded && !this.$store.commit('personalDataTravel/setTravelContext', travelContext);
    },

    dateOfBirthFormatted: {
      get() {
        return this.form.date_of_birth ? new Date(this.form.date_of_birth).toLocaleDateString() : '';
      },
      set(value) {
        this.form.date_of_birth = value;
      },
    },

    travelStartFormatted: {
      get() {
        return this.form.travel_start_date ? new Date(this.form.travel_start_date).toLocaleDateString() : '';
      },
      set(value) {
        this.form.travel_start_date = value;
      },
    },

    travelEndFormatted: {
      get() {
        return this.form.travel_end_date ? new Date(this.form.travel_end_date).toLocaleDateString() : '';
      },
      set(value) {
        this.form.travel_end_date = value;
      },
    },
  },
  filters: {
    capitalize: (value) => {
      if (!value) return '';
      const stringValue = value.toString();
      return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
    },
    formatNum(num) {
      return helpers.numberToString(num);
    },
  },
  async mounted() {
    // console.log('PersonalDetailsTravel - Component mounted, isAuthenticated:', this.isAuthenticated);

    // Initialize destination countries
    try {
      this.destinationCountries = TravelPolicyService.getDestinationCountries();
      // console.log('Destination countries loaded:', this.destinationCountries.length, 'countries');
    } catch (error) {
      // console.error('Error loading destination countries:', error);
      this.destinationCountries = [];
    }

    await this.getQuote();

    const agentCode = getReferralAgentCode(this.$route.query);
    if (agentCode) {
      validateReferralCode(agentCode).then((result) => {
        if (result) {
          this.referralFromQuery = true;
          this.form.agent_option = 2;
        }
      });
    }

    if (this.isAuthenticated) {
      // Check if user came through proper quote flow or accessed directly
      const hasQuoteData = this.$store.getters['rfq/getTravelQuoteResponse'];
      // console.log('PersonalDetailsTravel - hasQuoteData:', hasQuoteData);
      // console.log('PersonalDetailsTravel - hasQuoteData keys:', hasQuoteData ? Object.keys(hasQuoteData) : 'null');
      // console.log('PersonalDetailsTravel - hasQuoteData.id:', hasQuoteData?.id);

      // Check if quote data exists and has meaningful content
      const hasValidQuoteData = hasQuoteData
                               && (hasQuoteData.id || hasQuoteData.premium || hasQuoteData.coverage_amount
                                || Object.keys(hasQuoteData).length > 1); // More than just __ob__
      if (!hasValidQuoteData) {
        // console.log('PersonalDetailsTravel - No valid quote data found, but allowing access for direct entry');
        // Instead of redirecting, allow access but show a message to get a quote first
        this.$toast.info('Please get a travel quote first or the form will use default values', 'Quote Recommended');
      }
      // Pre-fill resident data from authentication
      this.form.phone_number = this.authenticatedUser.phone_number;
      this.form.first_name = this.authenticatedUser.given_name || this.authenticatedUser.name || '';
      this.form.last_name = this.authenticatedUser.family_name || '';
      this.form.email = this.authenticatedUser.email;

      // If we don't have separate first/last names, try to split the name
      if (!this.form.first_name && !this.form.last_name && this.authenticatedUser.name) {
        const nameParts = this.authenticatedUser.name.split(' ');
        this.form.first_name = nameParts[0] || '';
        this.form.last_name = nameParts.slice(1).join(' ') || '';
      }

      // If first_name contains the full name but last_name is empty, split it properly
      if (this.form.first_name && !this.form.last_name && this.form.first_name.includes(' ')) {
        const nameParts = this.form.first_name.trim().split(' ');
        this.form.first_name = nameParts[0] || '';
        this.form.last_name = nameParts[nameParts.length - 1] || '';
        // console.log('Split full name:', {
        //   original: this.authenticatedUser.name,
        //   first_name: this.form.first_name,
        //   last_name: this.form.last_name,
        // });
      }

      // Always pre-fill ID number and KRA PIN from authenticated user
      // console.log('PersonalDetailsTravel - DEBUG: authenticatedUser object:', this.authenticatedUser);
      // console.log('PersonalDetailsTravel - DEBUG: checking custom:id_number:', this.authenticatedUser['custom:id_number']);
      // console.log('PersonalDetailsTravel - DEBUG: checking custom:id-number:', this.authenticatedUser['custom:id-number']);
      // console.log('PersonalDetailsTravel - DEBUG: checking sub:', this.authenticatedUser.sub);

      this.form.id_number = this.authenticatedUser['custom:id_number']
                           || this.authenticatedUser['custom:id-number']
                           || this.authenticatedUser['custom:national_id']
                           || this.authenticatedUser.sub || '';

      this.form.kra_pin = this.authenticatedUser['custom:kra-pin']
                         || this.authenticatedUser['custom:kra_pin'] || '';

      // console.log('PersonalDetailsTravel - After initial prefill from auth:', {
      // id_number: this.form.id_number,
      // kra_pin: this.form.kra_pin,
      // });

      // Check if we have pre-filled form data from StartTravel
      const startTravelFormData = this.$store.getters['rfq/getTravelFormData'];
      // console.log('PersonalDetailsTravel - StartTravel form data:', startTravelFormData);
      // console.log('PersonalDetailsTravel - StartTravel form data keys:', startTravelFormData ? Object.keys(startTravelFormData) : 'null');

      // Debug: Check authenticated user data
      // const customFieldPrefix = 'custom:';
      // console.log('PersonalDetailsTravel - authenticated user data:', this.authenticatedUser);
      // console.log('PersonalDetailsTravel - custom:id_number:', this.authenticatedUser[`${customFieldPrefix}id_number`]);
      // console.log('PersonalDetailsTravel - custom:kra-pin:', this.authenticatedUser[`${customFieldPrefix}kra-pin`]);

      // Check all possible ID number fields
      // console.log('PersonalDetailsTravel - all custom fields:', Object.keys(this.authenticatedUser).filter((key) => key.startsWith(customFieldPrefix)));

      // Merge data from StartTravel (if available) but don't override critical fields
      if (startTravelFormData && Object.keys(startTravelFormData).length > 0) {
        // console.log('PersonalDetailsTravel - Using data from StartTravel');
        // Use form data from StartTravel but preserve critical fields if they're already set
        Object.keys(startTravelFormData).forEach((key) => {
          if (startTravelFormData[key] && key in this.form) {
            // console.log(`PersonalDetailsTravel - Processing ${key}: StartTravel=${startTravelFormData[key]}, Current=${this.form[key]}`);
            // For critical fields like ID number and KRA PIN, only override if current value is empty
            if ((key === 'id_number' || key === 'kra_pin') && this.form[key]) {
              // console.log(`PersonalDetailsTravel - Keeping authenticated user ${key}:`, this.form[key]);
              return;
            }
            this.form[key] = startTravelFormData[key];
            // console.log(`PersonalDetailsTravel - Set ${key} to:`, this.form[key]);
          }
        });
      } else {
        // console.log('PersonalDetailsTravel - No StartTravel form data, using authenticated user data only');
      }

      // Debug: Check what was actually filled
      // console.log('PersonalDetailsTravel - form after pre-fill:', {
      // id_number: this.form.id_number,
      // kra_pin: this.form.kra_pin,
      // phone_number: this.form.phone_number,
      // email: this.form.email,
      // });

      // Pre-fill date of birth if available
      if (this.authenticatedUser['custom:date_of_birth']) {
        this.form.date_of_birth = this.authenticatedUser['custom:date_of_birth'];
      }

      // Set gender from custom attributes if available
      if (this.authenticatedUser['custom:gender']) {
        this.form.gender = this.authenticatedUser['custom:gender'];
      }

      await this.updateQuoteWithEmail();

      // Update quote with personal information from authenticated user
      this.updateTravelContextWithPersonalInfo();
    }

    // Always populate data from quote if not already set
    if (this.quote) {
      if (!this.form.email) {
        this.form.email = this.quote.email_address;
      }
      if (!this.form.date_of_birth) {
        this.form.date_of_birth = this.quote.birth_date;
      }
      if (!this.form.destination_country) {
        this.form.destination_country = this.quote.selectedCountry;
      }

      // Pre-fill travel dates from quote
      if (this.quote.start_date) {
        this.form.travel_start_date = this.quote.start_date;
      }
      if (this.quote.end_date) {
        this.form.travel_end_date = this.quote.end_date;
      }

      this.calculateEndDate();
    }

    // Initialize Google Maps autocomplete
    await this.initializeAddressAutocomplete();
  },
  methods: {
    async getQuote() {
      const routeQuoteId = this.$route.query.q;
      const vuexQuoteData = this.$store.getters['rfq/getTravelQuoteResponse'];

      // console.log('getQuote - routeQuoteId:', routeQuoteId);
      // console.log('getQuote - vuexQuoteData:', vuexQuoteData);
      // Try to load from route parameter first
      if (routeQuoteId) {
        try {
          const response = await axios.get(`customer/digital-quotation/pacis-travel/quotes/${routeQuoteId}`);
          this.pageLoading = false;
          this.quote = response.data;
          // console.log('getQuote - loaded from API:', this.quote);
          // console.log('Quote selectedCountry field:', this.quote.selectedCountry);
          this.calculateEndDate();
        } catch (error) {
          // console.error('Error loading quote from API:', error);
          this.fallbackToVuexOrDefault();
        }
      } else if (vuexQuoteData && Object.keys(vuexQuoteData).length > 1) {
        // Fallback to Vuex store data
        // console.log('getQuote - using Vuex data');
        this.quote = vuexQuoteData;
        // console.log('Vuex Quote selectedCountry field:', this.quote.selectedCountry);
        this.pageLoading = false;
        this.calculateEndDate();
      } else {
        // No quote data available
        // console.log('getQuote - no quote data available');
        this.fallbackToVuexOrDefault();
      }
    },

    fallbackToVuexOrDefault() {
      this.pageLoading = false;
      // console.log('getQuote - no quote data available, redirecting user to create new quote');

      // No valid quote data available, redirect to quote creation
      this.$toast.error('No quote data found. Please create a new travel quote.', 'Quote Required');
      this.$router.push({
        name: 'Request',
        query: { b: 'travel' },
      });
    },
    async updateQuoteWithEmail() {
      try {
        const user = this.authenticatedUser;
        const quote = this.$route.query.q;

        // console.log('Attempting to update quote with email:', { quote, email: user.email });

        if (!quote) {
          // console.log('No quote ID available for email update, skipping');
          return;
        }

        const response = await axios.patch(`/customer/digital-quotation/pacis-travel/quotes/${quote}?email_address=${user.email}`);

        this.pageLoading = false;
        this.quote = response.data;
        this.calculateEndDate();
        // console.log('Quote updated successfully');
      } catch (error) {
        // console.warn('Failed to update quote with email:', error);
        this.pageLoading = false;

        // Don't block the user experience - just log the error and continue
        // The quote data should already be available from Vuex store
        // Handle different error types - but continue with cached data in all cases
        // console.log('Quote update error:', error.response?.status || error.message);

        // Don't throw the error - let the component continue loading
      }
    },
    /* eslint-disable sonarjs/no-duplicate-string */
    updateTravelContextWithPersonalInfo() {
      try {
        // console.log('Updating travel context with personal information');

        // For residents, use ID number; for non-residents, use passport number
        // When buying for someone else, always treat as non-resident (use passport)
        const user = this.authenticatedUser;
        const isResident = user && user['custom:id_number'] && !this.isBuyingForSomeoneElse;
        const identificationDocument = isResident
          ? user['custom:id_number']
          : (this.form.passport_number || 'UNKNOWN');

        // Get date of birth from form first, then from quote data as fallback
        const dateOfBirth = this.form.date_of_birth || this.quote?.birth_date || 'Unknown';

        // console.log('Date of birth for travel context:', {
        // 'form.date_of_birth': this.form.date_of_birth,
        // 'quote.birth_date': this.quote?.birth_date,
        // 'final dateOfBirth': dateOfBirth,
        // });

        // console.log('Quote data for family members:', {
        // 'quote.travel_type': this.quote?.travel_type,
        // 'quote.number_of_travelers': this.quote?.number_of_travelers,
        // 'quote.beneficiaries': this.quote?.beneficiaries,
        // 'quote keys': this.quote ? Object.keys(this.quote) : 'no quote',
        // });

        // Check if this is a family plan and create travel members accordingly
        const isFamily = this.quote?.travel_type?.toLowerCase() === 'family';
        const numberOfTravelers = this.quote?.number_of_travelers || 1;

        const travelMembers = [];
        if (isFamily && numberOfTravelers > 1) {
          // For family plans, create the main policyholder as the first member
          const mainMember = {
            insuredFirstName: this.form.first_name || 'Unknown',
            insuredSurname: this.form.last_name || 'Unknown',
            // insuredFirstName: 'TEST',
            // insuredSurname: 'TEST',
            insuredPassport: identificationDocument,
            insuredDateOfBirth: dateOfBirth,
            first_name: this.form.first_name,
            last_name: this.form.last_name,
            date_of_birth: dateOfBirth,
            passport_no: identificationDocument,
          };

          travelMembers.push(mainMember);

          // Use actual beneficiaries data if available from quote
          if (this.quote?.beneficiaries && Array.isArray(this.quote.beneficiaries)) {
            // console.log('Using actual beneficiaries from quote:', this.quote.beneficiaries);

            this.quote.beneficiaries.forEach((beneficiary, index) => {
              // Split name if it's a full name
              const nameParts = (beneficiary.name || '').trim().split(' ');
              const firstName = nameParts[0] || `Family Member ${index + 2}`;
              const lastName = nameParts.slice(1).join(' ') || this.form.last_name || 'Unknown';

              travelMembers.push({
                insuredFirstName: firstName,
                insuredSurname: lastName,
                // insuredFirstName: 'TEST',
                // insuredSurname: 'TEST',
                insuredPassport: beneficiary.passport_number || `TEMP${Date.now() + index + 1}`,
                insuredDateOfBirth: beneficiary.date_of_birth || dateOfBirth,
                first_name: firstName,
                last_name: lastName,
                date_of_birth: beneficiary.date_of_birth || dateOfBirth,
                passport_no: beneficiary.passport_number || `TEMP${Date.now() + index + 1}`,
                relationship: beneficiary.relationship,
                phone_number: beneficiary.phone_number,
              });
            });
          } else {
            // Fallback: Create placeholder family members
            // console.log('No beneficiaries in quote, creating placeholders');
            for (let i = 1; i < numberOfTravelers; i += 1) {
              travelMembers.push({
                insuredFirstName: `Family Member ${i + 1}`,
                insuredSurname: this.form.last_name || 'Unknown',
                // insuredFirstName: 'TEST',
                // insuredSurname: 'TEST',
                insuredPassport: `TEMP${Date.now() + i}`,
                insuredDateOfBirth: dateOfBirth, // Same as main for now
                first_name: `Family Member ${i + 1}`,
                last_name: this.form.last_name,
                date_of_birth: dateOfBirth,
                passport_no: `TEMP${Date.now() + i}`,
              });
            }
          }
        }

        const travelContext = {
          /* eslint-disable sonarjs/no-duplicate-string */
          insuredFirstName: this.form.first_name || 'Unknown',
          insuredSurname: this.form.last_name || 'Unknown',
          // insuredFirstName: 'TEST',
          // insuredSurname: 'TEST',
          insuredPassport: identificationDocument,
          insuredDateOfBirth: dateOfBirth,
          policyholderFirstName: this.form.first_name || 'Unknown',
          policyholderSurname: this.form.last_name || 'Unknown',
          policyholderPassport: identificationDocument,
          policyholderDateOfBirth: dateOfBirth,

          // Buying for someone else specific context
          isBuyingForSomeoneElse: this.isBuyingForSomeoneElse,

          // If buying for someone else, the buyer information should be stored separately
          // This will be populated during payment flow from the buyer's details
          buyerInformation: this.isBuyingForSomeoneElse ? {
            // This will be filled during payment from the buyer form
            // For now, we just mark that this is a buying scenario
            note: 'Buyer details will be collected during payment process',
          } : null,

          // Include travel members for family plans
          travelMembers: travelMembers.length > 0 ? travelMembers : null,

          // Include complete form data for policy issuance (traveler's data)
          email: this.form.email,
          phone_number: this.form.phone_number,
          gender: this.form.gender,
          id_number: this.form.id_number,
          passport_number: this.form.passport_number,
          kra_pin: this.form.kra_pin,
          emergency_contact_name: this.form.emergency_contact_name,
          emergency_contact_phone: this.form.emergency_contact_phone,
          next_of_kin_relationship: this.form.next_of_kin_relationship,
          travel_start_date: this.form.travel_start_date,
          travel_end_date: this.form.travel_end_date,
          address: this.form.address,
          agent_option: this.form.agent_option,
          politically_exposed: this.form.politically_exposed,
          declaration_acceptance: this.form.declaration_acceptance,
          data_privacy_consent: this.form.data_privacy_consent,
          coordinates: this.form.coordinates,
          date_of_birth: dateOfBirth, // Also include as direct field for easier access
          selectedCountry: this.form.destination_country || this.quote?.destination_country || null,
          /* eslint-disable sonarjs/no-duplicate-string */
        };

        // console.log('Travel context created:', {
        // isFamily,
        // numberOfTravelers,
        // travelMembersCount: travelMembers.length,
        // travelMembers,
        // });

        // console.log('Form data for travel context update:', {
        // first_name: this.form.first_name,
        // last_name: this.form.last_name,
        // passport_number: this.form.passport_number,
        // email: this.form.email,
        // phone_number: this.form.phone_number,
        // date_of_birth: this.form.date_of_birth,
        // });

        // Update travel context in store
        this.$store.commit('personalDataTravel/setTravelContext', travelContext);
        // console.log('Travel context updated successfully:', travelContext);
        // console.log('Travel context selectedCountry:', travelContext.selectedCountry);
      } catch (error) {
        // console.error('Failed to update travel context:', error);
      }
    },
    onUploadSuccess(uploadData) {
      // console.log('Upload success:', uploadData);
      const { documentType } = uploadData;
      // Track which documents have been uploaded
      if (documentType === 'passport') {
        this.documentsUploaded.passport = true;
      } else if (documentType === 'signature') {
        this.documentsUploaded.signature = true;
      } else if (documentType === 'idNumber') {
        this.documentsUploaded.idNumber = true;
      } else if (documentType === 'kraPinNumber') {
        this.documentsUploaded.kraPin = true;
      }
    },
    async proceed() {
      // Check if all required documents are uploaded
      if (!this.areRequiredDocumentsUploaded) {
        const missingDocs = [];
        if (!this.documentsUploaded.passport) missingDocs.push('Passport');
        if (!this.documentsUploaded.signature) missingDocs.push('Digital Signature');

        // For authenticated users (residents) or when not buying for someone else, require all documents
        if (this.isAuthenticated && !this.isBuyingForSomeoneElse) {
          if (!this.documentsUploaded.idNumber) missingDocs.push('ID Copy');
          if (!this.documentsUploaded.kraPin) missingDocs.push('KRA PIN');
        }

        const docList = missingDocs.join(', ');
        this.$toast.error(
          `Please upload all required documents: ${docList}`,
          'Missing Documents',
          { timeout: 8000 },
        );
        return; // Stop execution if documents are missing
      }

      this.loading = true;

      try {
        // Ensure quote has personal information before creating policy
        this.updateTravelContextWithPersonalInfo();
        // Include KRA pin for residents in the policy data
        const updateData = {
          ...this.form,
          pin_number: this.isAuthenticated ? this.form.kra_pin : null,
        };
        delete updateData.email;

        const resp = await axios.post('customer/digital-quotation/pacis-travel/policy', {
          ...updateData,
          ...{ quote_id: this.quote.id },
        });

        this.loading = false;

        if (this.isBuyingForSomeoneElse) {
          // For buying for someone else, redirect to the BuyForSomeoneElseCover page for buyer details and payment
          const paymentContext = {
            productType: 'travel',
            productId: this.quote.id,
            policyData: resp.data,
            quoteData: this.quote,
            formData: this.form, // This contains the traveler's information
            isBuyingForSomeoneElse: true,
          };
          this.$store.commit('payments/setPaymentContext', paymentContext);

          // Redirect to the buying for someone else cover page
          this.$router.push({
            name: 'BuyForSomeoneElseCover',
            query: { q: this.quote.id },
          });
        } else if (this.isAuthenticated) {
          // For residents, use the same payment flow as health/DP products
          const user = this.authenticatedUser;
          const payment = {
            payableAmount: this.quote.premium,
            kraPin: user['custom:kra-pin'],
            transactionReference: travelPaymentReference(this.form.id_number || this.form.id_no || this.form.passport_number),
            customerPhoneNumber: user.phone_number,
            transactionDescription: `TV_rfq-${this.quote.id}`,
          };
          const quoteInfo = {
            coverType: 'Travel Insurance Cover',
            payPercentage: 100,
          };

          // Set payment context for travel insurance policy auto-issuance after payment
          const paymentContext = {
            productType: 'travel',
            productId: this.quote.id, // Use quote ID for policy lookup
            policyData: resp.data,
            quoteData: this.quote,
            formData: this.form,
          };
          this.$store.commit('payments/setPaymentContext', paymentContext);

          // Emit to parent component for payment processing (following health/DP pattern)
          this.$emit('to-make-payment', { data: { payment, quoteInfo } });
        } else {
          // For non-residents, redirect to STK payment with customerLogin authentication
          const payment = {
            payableAmount: this.quote.premium,
            product: 'Travel Insurance',
            productType: 'travel',
            productId: this.quote.id,
            email: this.quote.email_address,
            phoneNumber: this.form.phone_number,
            transactionReference: travelPaymentReference(this.form.id_number || this.form.id_no || this.form.passport_number),
            transactionDescription: `TV_rfq-${this.quote.id}`,
            // Store policy and quote data for post-payment Mapfre issuance
            policyData: resp.data,
            quoteData: this.quote,
            formData: this.form,
          };
          this.$store.commit('payments/setPaymentContext', payment);
          this.$router.push({ name: 'PaymentSelection' });
        }
      } catch (error) {
        this.loading = false;
        // console.error('Policy creation error:', error);

        let errorMessage = 'Failed to create travel insurance policy. ';

        if (error.response?.data?.message) {
          errorMessage += error.response.data.message;
        } else if (error.message) {
          errorMessage += error.message;
        } else {
          errorMessage += 'Please try again or contact support.';
        }

        this.$toast.error(errorMessage, 'Policy Creation Failed');
      }
    },

    // Enhanced methods
    getFormCompletionPercentage() {
      let totalFields = 16; // Base fields
      let completedFields = 0;

      if (this.form.first_name) completedFields += 1;
      if (this.form.last_name) completedFields += 1;
      if (this.form.date_of_birth) completedFields += 1;
      if (this.form.email) completedFields += 1;
      if (this.form.gender) completedFields += 1;
      if (this.form.phone_number) completedFields += 1;
      // Address is complete if coordinates are set OR if manual address is long enough
      if ((this.form.address && this.form.coordinates) || (this.form.address && typeof this.form.address === 'string' && this.form.address.trim().length >= 10)) completedFields += 1;
      if (this.form.emergency_contact_name) completedFields += 1;
      if (this.form.emergency_contact_phone) completedFields += 1;
      if (this.form.next_of_kin_relationship) completedFields += 1;
      if (this.form.politically_exposed !== null && this.form.politically_exposed !== undefined) completedFields += 1;
      if (this.form.travel_start_date) completedFields += 1;
      if (this.form.travel_end_date) completedFields += 1;
      if (this.form.declaration_acceptance) completedFields += 1;
      if (this.form.data_privacy_consent) completedFields += 1;
      if (this.form.agent_option) completedFields += 1;

      // Add fields specific to scenario
      if (this.isBuyingForSomeoneElse) {
        // For buying for someone else, passport is more important
        totalFields += 1; // Passport number
        if (this.form.passport_number) completedFields += 1;

        // ID and KRA PIN are optional but count if filled
        if (this.form.kra_pin) {
          totalFields += 1;
          completedFields += 1;
        }
        if (this.form.id_number) {
          totalFields += 1;
          completedFields += 1;
        }
      } else if (this.isAuthenticated) {
        // Add resident-specific required fields
        totalFields += 2; // KRA pin and ID number
        if (this.form.kra_pin) completedFields += 1;
        if (this.form.id_number) completedFields += 1;
      }

      return Math.round((completedFields / totalFields) * 100);
    },

    getFormErrors() {
      const errors = [];

      if (!this.form.first_name) errors.push('First Name');
      if (!this.form.last_name) errors.push('Last Name');
      if (!this.form.date_of_birth) errors.push('Date of Birth');
      if (!this.form.email) errors.push('Email Address');
      if (!this.form.gender) errors.push('Gender');
      if (!this.form.phone_number) errors.push('Mobile Number (Main)');
      // Check address - allow either coordinates from Google autocomplete or manual address entry
      if (!this.form.address || (!this.form.coordinates && (typeof this.form.address !== 'string' || this.form.address.trim().length < 10))) {
        errors.push('Residential Address (must be at least 10 characters or use Google Maps autocomplete)');
      }
      if (!this.form.emergency_contact_name) errors.push('Next of Kin Name');
      if (!this.form.emergency_contact_phone) errors.push('Next of Kin Phone');
      if (!this.form.next_of_kin_relationship) errors.push('Next of Kin Relationship');
      if (this.form.politically_exposed === null || this.form.politically_exposed === undefined) {
        errors.push('Political Exposure (please select Yes or No)');
      }
      if (!this.form.travel_start_date) errors.push('Travel Start Date');
      if (!this.form.travel_end_date) errors.push('Travel End Date');
      if (!this.form.declaration_acceptance) {
        errors.push('Declaration & Consent (must be checked)');
      }
      if (!this.form.data_privacy_consent) {
        errors.push('Data Privacy Consent (must be checked)');
      }
      if (!this.form.agent_option) errors.push('Referral Information');

      // Add resident-specific validation - but only when NOT buying for someone else
      if (this.isAuthenticated && !this.isBuyingForSomeoneElse) {
        if (!this.form.kra_pin) errors.push('KRA PIN');
        if (!this.form.id_number) errors.push('ID Number');
      }

      // For buying for someone else, passport is more important than ID
      if (this.isBuyingForSomeoneElse && !this.form.passport_number) {
        errors.push('Passport Number (required for traveler identification)');
      }

      // Debug logging to help identify issues
      // console.log('Form validation check:', {
      // politically_exposed: this.form.politically_exposed,
      // declaration_acceptance: this.form.declaration_acceptance,
      // data_privacy_consent: this.form.data_privacy_consent,
      // agent_option: this.form.agent_option,
      // isBuyingForSomeoneElse: this.isBuyingForSomeoneElse,
      // errors,
      // });

      return errors;
    },

    async initializeAddressAutocomplete() {
      try {
        const google = await loader.load();
        const input = document.getElementById('address');
        if (input) {
          // Use the same configuration for both residents and non-residents
          // This ensures address autocomplete works consistently
          const autocompleteOptions = {
            types: ['address'],
            // Optional: prefer Kenya for residents but allow worldwide
            ...(this.isAuthenticated && { componentRestrictions: { country: 'ke' } }),
          };

          const autocomplete = new google.maps.places.Autocomplete(input, autocompleteOptions);

          google.maps.event.addListener(autocomplete, 'place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry) {
              this.form.coordinates = {
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
              };
              // Also update the address field value
              this.form.address = place.formatted_address || input.value;
            }
          });
        }
      } catch (error) {
        // console.warn('Google Maps failed to load:', error);
      }
    },

    isObjectField() {
      return () => {
        // Allow if coordinates are set from Google autocomplete
        if (this.form.coordinates && typeof this.form.coordinates === 'object') {
          return true;
        }
        // Also allow if address field is filled (manual entry)
        if (this.form.address && typeof this.form.address === 'string' && this.form.address.trim().length >= 10) {
          return true;
        }
        return 'Please enter a complete address or select from suggestions';
      };
    },

    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return 'N/A';

      try {
        const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${start} - ${end}`;
      } catch (error) {
        return `${startDate} - ${endDate}`;
      }
    },

    transformToMapfrePayload(quoteData, formData) {
      // Map destination to zone and product code
      const { zone, prodCode } = this.getZoneAndProductCode(quoteData);

      // Build insured data array matching backend structure
      const insuredData = [{
        insuredFirstName: formData.first_name,
        insuredSurname: formData.last_name,
        // insuredFirstName: 'TEST',
        // insuredSurname: 'TEST',
        insuredDateOfBirth: formData.date_of_birth,
        insuredPassport: formData.passport_number || `TEMP${Date.now()}`,
      }];

      // Build payload matching IssuePolicyRequest structure
      const payload = {
        prodCode,
        policyNumber: 'AUTO',
        email: formData.email || quoteData.email_address,
        startDate: formData.travel_start_date || quoteData.start_date,
        endDate: formData.travel_end_date || quoteData.end_date,
        agnShtDesc: formData.agent_option === 1 ? 'DIRECT' : 'AGENT',
        comment: `Travel to ${quoteData.destination} - Portal booking`,
        zone: zone.toString(),
        destinationCountry: this.getCountryCode(quoteData.destination),
        originCountry: 'KE',
        insuredData,
        policyholderFirstName: formData.first_name,
        policyholderSurname: formData.last_name,
        policyholderPassport: formData.passport_number || `TEMP${Date.now()}`,
        policyholderDateOfBirth: formData.date_of_birth,
        premium: quoteData.premium?.toString() || '0',
        coverageLimits: this.getCoverageLimits(prodCode),
        hidePremium: true,
      };

      // Add zone-specific fields
      if (quoteData.coverage_amount) {
        payload.medicalExpenseAmount = quoteData.coverage_amount.toString();
      }

      return payload;
    },

    getZoneAndProductCode(quoteData) {
      const destination = quoteData.destination?.toLowerCase() || '';
      const travelType = quoteData.travel_type?.toLowerCase() || '';
      const cover = quoteData.coverage_amount || 0;

      // EUROPE
      if (this.isEuropeDestination(destination)) {
        // Basic, Family Basic, Family Plus, Individual Plus
        if (travelType === 'family') {
          if (cover >= 150000) return { zone: 2, prodCode: 'EUR_FAM_PLU' };
          return { zone: 2, prodCode: 'EUR_FAM_BAS' };
        }
        if (cover >= 150000) return { zone: 2, prodCode: 'EUR_IND_PLU' };
        return { zone: 2, prodCode: 'EUR_BAS' };
      }

      // ASIA
      if (this.isAsiaDestination(destination)) {
        return {
          zone: 12,
          prodCode: travelType === 'family' ? 'ASI_FAM' : 'ASI_IND',
        };
      }

      // AMERICAS / WORLDWIDE
      if (this.isAmericasDestination(destination)) {
        // Basic, Extended, Premium versions
        if (travelType === 'family') {
          if (cover >= 200000) return { zone: 3, prodCode: 'WW_FAM_PRE' };
          if (cover >= 150000) return { zone: 3, prodCode: 'WW_FAM_EXT' };
          return { zone: 3, prodCode: 'WW_FAM_BAS' };
        }
        if (cover >= 200000) return { zone: 3, prodCode: 'WW_IND_PRE' };
        if (cover >= 150000) return { zone: 3, prodCode: 'WW_IND_EXT' };
        return { zone: 3, prodCode: 'WW_IND_BAS' };
      }

      // No match found
      return null;
    },
    getCountryCode(destination) {
      if (!destination) return null;

      // Get comprehensive country list
      const countries = this.getDestinationCountries();
      const destinationLower = destination.toLowerCase();

      // Try to find exact match first
      const exactMatch = countries.find((country) => country.TRC_NAME.toLowerCase() === destinationLower);
      if (exactMatch) {
        return exactMatch.TRC_SHT_DESC;
      }

      // Try partial matching
      const partialMatch = countries.find((country) => country.TRC_NAME.toLowerCase().includes(destinationLower)
        || destinationLower.includes(country.TRC_NAME.toLowerCase()));
      if (partialMatch) {
        return partialMatch.TRC_SHT_DESC;
      }

      return null; // Return null if no match found
    },

    getCoverageLimits(prodCode) {
      const baseCoverage = {
        medicalExpenses: '1',
        personalAssistance: '1',
        baggage: '1',
        travelDelay: '1',
      };

      if (prodCode.startsWith('EUR_')) {
        return {
          ...baseCoverage,
          winterSport: '1',
          hospitalization: '1',
          medicalTransportation: '1',
          accidentalDamagePhoneGadget: '1',
          curtailment: '1',
        };
      }

      if (prodCode === 'CORP_BUS') {
        return {
          ...baseCoverage,
          curtailment: '1',
          medicalTransportation: '1',
          personalLiability: '1',
          advanceOfBailBondLiability: '1',
          cancellation: '1',
        };
      }

      return baseCoverage;
    },

    getDestinationCountries() {
      return this.destinationCountries;
    },

    calculateEndDate() {
      if (!this.quote) return;

      // Calculate end date if start date and duration are available but end date is not
      if (this.quote.start_date && this.quote.duration && !this.quote.end_date) {
        const startDate = new Date(this.quote.start_date);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + parseInt(this.quote.duration, 10));
        [this.quote.end_date] = endDate.toISOString().split('T');

        // console.log(`Travel end date calculated: ${this.quote.end_date} (${this.quote.duration} days from ${this.quote.start_date})`);
      }

      // Always update form fields with quote dates (calculated or existing)
      if (this.quote.start_date) {
        this.form.travel_start_date = this.quote.start_date;
      }
      if (this.quote.end_date) {
        this.form.travel_end_date = this.quote.end_date;
      }
    },

    // Form data persistence methods for login redirect
    storeFormData() {
      // Store current form data in localStorage before redirect to login
      const formData = {
        ...this.form,
        quote_id: this.quote?.id,
        timestamp: Date.now(),
      };
      localStorage.setItem('travel_form_backup', JSON.stringify(formData));
    },

    restoreFormData() {
      // Restore form data after login redirect
      try {
        const storedData = localStorage.getItem('travel_form_backup');
        if (storedData) {
          const formData = JSON.parse(storedData);
          // Check if data is not too old (24 hours)
          if (Date.now() - formData.timestamp < 24 * 60 * 60 * 1000) {
            // Restore non-authentication fields only
            Object.keys(this.form).forEach((key) => {
              if (!['email', 'id_number', 'date_of_birth', 'gender'].includes(key) && formData[key]) {
                this.form[key] = formData[key];
              }
            });
            // console.log('Form data restored from localStorage');
          }
          // Clean up old data
          localStorage.removeItem('travel_form_backup');
        }
      } catch (error) {
        // console.warn('Could not restore form data:', error);
      }
    },

    validateDateOfBirth() {
      return () => {
        if (!this.quote?.birth_date || !this.form.date_of_birth) {
          return true; // Skip validation if quote data not available
        }

        const quoteBirthDate = new Date(this.quote.birth_date).toISOString().slice(0, 10);
        const formBirthDate = new Date(this.form.date_of_birth).toISOString().slice(0, 10);

        if (quoteBirthDate !== formBirthDate) {
          return `Date of birth must match quote data (${new Date(this.quote.birth_date).toLocaleDateString()})`;
        }

        return true;
      };
    },

  },

  // Add watchers for form data persistence
  watch: {
    formIsValid(isValid) {
      // If form becomes valid and user came from quote summary, redirect to payment
      if (isValid && this.$route.query.returnTo === 'payment') {
        // console.log('Form completed, redirecting back to payment flow');
        this.$toast.success('Personal details completed! Proceeding to payment.');

        // Small delay to let the user see the success message
        setTimeout(() => {
          this.$router.push({
            name: 'TravelQuoteSummary',
            query: {
              q: this.$route.query.q,
              fromPersonalDetails: 'true',
            },
          });
        }, 1500);
      }
    },
    form: {
      deep: true,
      handler(newForm, oldForm) {
        // Auto-save form data as user types (debounced)
        if (this.autoSaveTimeout) {
          clearTimeout(this.autoSaveTimeout);
        }
        this.autoSaveTimeout = setTimeout(() => {
          if (!this.isAuthenticated) {
            this.storeFormData();
          }

          // Check if personal information fields changed and update quote
          const personalFields = ['first_name', 'last_name', 'passport_number', 'email', 'phone_number', 'date_of_birth'];
          const hasPersonalChanges = personalFields.some((field) => {
            const oldValue = oldForm?.[field] || '';
            const newValue = newForm?.[field] || '';
            return oldValue !== newValue && newValue.trim() !== '';
          });

          if (hasPersonalChanges && this.quote?.id) {
            // console.log('Personal information changed, updating quote...');
            this.updateTravelContextWithPersonalInfo();
          }
        }, 2000); // Save after 2 seconds of no changes
      },
    },
  },

  // Restore form data when component is created
  created() {
    // console.log('PersonalDetailsTravel - Component CREATED');
    this.restoreFormData();

    // Make update function available for testing
    if (typeof window !== 'undefined') {
      window.updateTravelContextWithPersonalInfo = () => this.updateTravelContextWithPersonalInfo();
      window.getFormData = () => this.form;
    }
  },
};
</script>
